/**
 * Deploy 3 · P1+P3 — arrastre de bloques (mantener presionado) en la agenda del perfil.
 * Portado de AgendaDayView: long-press 320 ms, snap 15 min, preview, scroll táctil vs. drag
 * (touchAction pan-y + preventDefault mientras hay gesto). Añade: estiramiento del grid al
 * acercarse al borde (owner, fuera de horario) y auto-scroll cerca del borde del viewport.
 */
import { useRef, useState, useCallback } from 'react';

const LONG_PRESS_MS = 320;
const SNAP = 5;
const EDGE_PX = 56;
export const STRETCH_MIN = 60;
export const DRAGGABLE_STATUSES = ['pending', 'confirmed', 'completed']; // D5 · B: completadas solo cambian fecha/hora
export const canDragBooking = (b) => DRAGGABLE_STATUSES.includes(b?.status);

const toMin = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };

export const useBlockDrag = ({ enabled, pph, boundsRef, onStretch, onDrop }) => {
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const activeRef = useRef(false);
  const suppressTap = useRef(false);
  const set = (v) => { dragRef.current = v; setDrag(v); };
  const unfreeze = useCallback(() => { if (dragRef.current) dragRef.current.frozen = false; }, []);
  const consumeTap = useCallback(() => { const s = suppressTap.current; suppressTap.current = false; return s; }, []);

  const onBlockPointerDown = useCallback((booking, e) => {
    if (!enabled || !canDragBooking(booking) || dragRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const x0 = e.clientX; const y0 = e.clientY;
    let timer = null; let edgeTimer = null; let lastY = y0;
    activeRef.current = true;
    suppressTap.current = false;

    const stopEdge = () => { if (edgeTimer) { clearInterval(edgeTimer); edgeTimer = null; } };
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      stopEdge();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      document.body.style.userSelect = '';
      activeRef.current = false;
    };
    const compute = (st, clientY) => {
      const dur = st.base.end - st.base.start;
      const raw = st.base.start + ((clientY + window.scrollY) - st.startDocY) * (60 / pph);
      return Math.max(0, Math.min(Math.round(raw / SNAP) * SNAP, 1440 - dur));
    };
    const track = (clientY) => {
      const st = dragRef.current;
      if (!st || st.frozen) return;
      const newStart = compute(st, clientY);
      const dur = st.base.end - st.base.start;
      const { ws, we } = boundsRef.current;
      const now = Date.now();
      if (newStart <= ws + SNAP && ws > 0 && now - (st.lastStretch || 0) > 250) {
        st.lastStretch = now; st.frozen = true;
        st.startDocY += (STRETCH_MIN / 60) * pph;
        onStretch('before');
      } else if (newStart + dur >= we - SNAP && we < 1440 && now - (st.lastStretch || 0) > 250) {
        st.lastStretch = now;
        onStretch('after');
      }
      if (newStart !== st.newStart) set({ ...st, newStart });
    };
    const onMove = (ev) => {
      const st = dragRef.current;
      if (!st) {
        if (Math.abs(ev.clientY - y0) > 10 || Math.abs(ev.clientX - x0) > 10) cleanup(); // es scroll, no drag
        return;
      }
      lastY = ev.clientY;
      track(ev.clientY);
      const vh = window.innerHeight;
      const dir = ev.clientY > vh - EDGE_PX ? 1 : ev.clientY < EDGE_PX ? -1 : 0;
      if (dir && !edgeTimer) {
        edgeTimer = setInterval(() => { window.scrollBy(0, dir * 8); track(lastY); }, 16);
      } else if (!dir) stopEdge();
    };
    const onUp = () => {
      const st = dragRef.current;
      cleanup();
      set(null);
      if (st && st.newStart !== st.base.start) onDrop(st.booking, st.newStart);
    };
    const onCancel = () => { cleanup(); set(null); };

    timer = setTimeout(() => {
      timer = null;
      suppressTap.current = true;
      document.body.style.userSelect = 'none';
      try { navigator.vibrate?.(20); } catch { /* noop */ }
      const base = { start: toMin(booking.start), end: toMin(booking.end) };
      set({ id: booking.booking_id, booking, base, startDocY: y0 + window.scrollY, newStart: base.start, frozen: false });
    }, LONG_PRESS_MS);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }, [enabled, pph, boundsRef, onStretch, onDrop]);

  return { drag, activeRef, onBlockPointerDown, consumeTap, unfreeze };
};
